import { ensureError } from '#errors/ensure';
import { raise } from '#errors/raise';
import { isNotDefined } from '#misc/is-not-defined.guard';

/**
 * An iterator implementation for traversing over a single item or an array of items.
 *
 * @typeParam TData - The type of data to iterate over.
 *
 * @example
 * ```typescript
 * const iterator = new ResultIterator([1, 2, 3]);
 * let result = iterator.next(); // { done: false, value: 1 }
 * result = iterator.next();     // { done: false, value: 2 }
 * result = iterator.next();     // { done: false, value: 3 }
 * result = iterator.next();     // { done: true, value: undefined }
 * ```
 *
 * @remarks
 * - The iterator consumes its buffer as items are retrieved.
 * - The `throw` method raises an error if provided, or throws if no error is given.
 */
export class ResultIterator<TData> implements Iterator<TData> {
  private buffer: TData[] = [];

  constructor(data: TData | TData[]) {
    this.buffer = Array.isArray(data) ? [...data] : [data];
  }

  next(): IteratorResult<TData> {
    if (this.buffer.length > 0) {
      return {
        done: false,
        value: this.buffer.shift() as TData,
      };
    }

    return {
      done: true,
      value: undefined,
    };
  }

  throw(e?: unknown): IteratorResult<TData> {
    if (isNotDefined(e)) {
      raise('Iterator error: No error provided.');
    }

    throw ensureError(e);
  }
}
